<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>
    <template v-else-if="contract">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h3 class="mb-50">
              {{ contract.contract_number }}
            </h3>
            <div>{{ projectLabel }}</div>
            <b-badge
              class="mt-50"
              :variant="statusVariant"
            >
              {{ enumLabel(contract.status, $i18n.locale) }}
            </b-badge>
            <b-badge
              v-if="isLoss"
              variant="danger"
              class="mt-50 ml-50"
            >
              {{ $t('ledger_ct_loss') }}
            </b-badge>
            <b-badge
              v-if="positionEnded"
              variant="dark"
              class="mt-50 ml-50"
            >
              {{ $t('ledger_ct_ended') }}
            </b-badge>
            <b-badge
              v-if="unearnedRevenue > 0"
              variant="warning"
              class="mt-50 ml-50"
            >
              {{ $t('ledger_ct_billed_ahead') }} {{ money(unearnedRevenue) }}
            </b-badge>
            <b-badge
              v-if="unbilledRevenue > 0"
              variant="info"
              class="mt-50 ml-50"
            >
              {{ $t('ledger_ct_unbilled') }} {{ money(unbilledRevenue) }}
            </b-badge>
          </div>
          <div class="d-none d-md-flex flex-wrap">
            <b-button
              v-if="canEdit"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              variant="outline-primary"
              :to="{ name: 'ledger-contracts-edit', params: { id: contract.id } }"
            >
              {{ $t('ledger_ct_edit') }}
            </b-button>
            <b-button
              v-if="canBill"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              variant="primary"
              @click="openBilling"
            >
              {{ $t('ledger_ct_billing') }}
            </b-button>
            <b-button
              v-if="canHandover"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              variant="outline-success"
              @click="confirmHandover"
            >
              {{ $t('ledger_ct_handover') }}
            </b-button>
            <b-button
              v-if="canRelease"
              size="sm"
              class="mb-50 rounded-pill"
              variant="outline-primary"
              @click="confirmRelease"
            >
              {{ $t('ledger_ct_release') }}
            </b-button>
          </div>
        </div>
        <div class="ledger-ct-kpis mt-1">
          <div
            v-for="card in cards"
            :key="card.label"
          >
            <span class="text-muted">{{ card.label }}</span>
            <strong class="ledger-num d-block">{{ card.value }}</strong>
          </div>
        </div>
        <div class="mt-1">
          <div class="d-flex justify-content-between">
            <span>{{ $t('ledger_ct_percent') }}</span>
            <span class="ledger-num">{{ percentLabel }}</span>
          </div>
          <b-progress
            :value="percentValue"
            :max="100"
            class="mt-50"
          />
        </div>
        <p
          v-if="hasDraft"
          class="text-warning mt-1 mb-0"
        >
          {{ $t('ledger_ct_draft_exists') }}
        </p>
      </b-card>

      <b-card class="rounded-lg">
        <h5>{{ $t('ledger_ct_billings') }}</h5>
        <div
          v-if="!(contract.billings || []).length"
          class="text-muted"
        >
          {{ $t('ledger_inv_empty') }}
        </div>
        <div
          v-else
          class="d-none d-md-block"
        >
          <b-table
            :items="contract.billings"
            :fields="billingFields"
            small
            responsive
            class="ledger-dense-table"
          >
            <template #cell(invoice_number)="data">
              <b-link
                v-if="hasPermission('view invoices')"
                :to="{ name: 'ledger-invoices-view', params: { id: data.item.id } }"
              >
                {{ data.item.invoice_number }}
              </b-link>
              <span v-else>{{ data.item.invoice_number }}</span>
            </template>
            <template #cell(cumulative_work_value)="data">
              <span class="ledger-num">{{ money(data.item.cumulative_work_value) }}</span>
            </template>
            <template #cell(total_amount)="data">
              <span class="ledger-num">{{ money(data.item.total_amount) }}</span>
            </template>
            <template #cell(retention_amount)="data">
              <span class="ledger-num">{{ money(data.item.retention_amount) }}</span>
            </template>
            <template #cell(collectible_amount)="data">
              <span class="ledger-num">{{ money(data.item.collectible_amount) }}</span>
            </template>
            <template #cell(balance_due)="data">
              <span class="ledger-num">{{ money(data.item.balance_due) }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="invoiceVariant(data.item.status)">
                {{ enumLabel(data.item.status, $i18n.locale) }}
              </b-badge>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="row in contract.billings"
            :key="row.id"
            class="ledger-ct-card"
          >
            <b-link
              v-if="hasPermission('view invoices')"
              :to="{ name: 'ledger-invoices-view', params: { id: row.id } }"
            >
              {{ row.invoice_number }}
            </b-link>
            <div class="ledger-num">
              {{ $t('ledger_ct_net') }} {{ money(row.collectible_amount) }}
            </div>
            <div class="ledger-num">
              {{ $t('ledger_inv_balance') }} {{ money(row.balance_due) }}
            </div>
          </article>
        </div>
      </b-card>
    </template>

    <b-modal
      v-model="billingOpen"
      :title="$t('ledger_ct_billing')"
      hide-footer
    >
      <b-form-group :label="$t('ledger_ct_cumulative')">
        <b-form-input
          v-model.number="billing.cumulative_work_value"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_inv_date')">
        <b-form-input
          v-model="billing.billing_date"
          type="date"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_inv_due')">
        <b-form-input
          v-model="billing.due_date"
          type="date"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_assets_notes')">
        <b-form-input v-model="billing.notes" />
      </b-form-group>
      <div
        v-if="billingPreview"
        class="ledger-num mb-1"
      >
        <div>{{ $t('ledger_ct_previous') }} {{ money(billingPreview.billed) }}</div>
        <div>{{ $t('ledger_ct_gross') }} {{ money(billingPreview.gross) }}</div>
        <div>{{ $t('ledger_ct_retention') }} {{ money(billingPreview.retention) }}</div>
        <strong>{{ $t('ledger_ct_net') }} {{ money(billingPreview.net) }}</strong>
      </div>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="acting || !canSubmitBilling"
        @click="submitBilling"
      >
        {{ $t('ledger_ct_billing') }}
      </b-button>
    </b-modal>

    <div
      v-if="contract"
      class="ledger-ct-sticky d-md-none"
    >
      <b-button
        v-if="canBill"
        size="sm"
        variant="primary"
        class="rounded-pill"
        @click="openBilling"
      >
        {{ $t('ledger_ct_billing') }}
      </b-button>
      <b-button
        v-if="canHandover"
        size="sm"
        variant="outline-success"
        class="rounded-pill"
        @click="confirmHandover"
      >
        {{ $t('ledger_ct_handover') }}
      </b-button>
      <b-button
        v-if="canRelease"
        size="sm"
        variant="outline-primary"
        class="rounded-pill"
        @click="confirmRelease"
      >
        {{ $t('ledger_ct_release') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BSpinner, BProgress, BTable, BLink, BModal, BFormGroup, BFormInput,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getContract, createContractBilling, handoverContract, releaseContractRetention,
} from '@/api/accounting/contracts'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, todayIso, apiErrorMessage, ledgerMessageKey,
  glAccountCode,
} from '@/utils/ledger'

const STATUS_VARIANT = { active: 'success', handed_over: 'info', closed: 'dark' }
const INVOICE_VARIANT = {
  draft: 'secondary', issued: 'info', partially_paid: 'warning', paid: 'success', cancelled: 'dark',
}

function addDays(iso, days) {
  const date = new Date(`${iso}T00:00:00`)
  date.setDate(date.getDate() + days)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export default {
  name: 'ContractShow',
  components: {
    BCard, BBadge, BButton, BSpinner, BProgress, BTable, BLink, BModal, BFormGroup, BFormInput,
  },
  data() {
    return {
      loading: false,
      acting: false,
      contract: null,
      billingOpen: false,
      billing: {
        cumulative_work_value: null,
        billing_date: todayIso(),
        due_date: addDays(todayIso(), 30),
        notes: '',
      },
    }
  },
  computed: {
    statusValue() {
      return enumValue(this.contract && this.contract.status)
    },
    statusVariant() {
      return STATUS_VARIANT[this.statusValue] || 'secondary'
    },
    position() {
      return (this.contract && this.contract.position) || {}
    },
    unearnedRevenue() {
      const value = this.position.unearned_revenue != null
        ? this.position.unearned_revenue
        : this.position.billed_ahead
      return Number(value) || 0
    },
    unbilledRevenue() {
      const value = this.position.unbilled_revenue != null
        ? this.position.unbilled_revenue
        : this.position.unbilled_work
      return Number(value) || 0
    },
    positionEnded() {
      return this.position.ended === true
    },
    isLoss() {
      return Number(this.contract && this.contract.expected_profit) < 0
    },
    percentValue() {
      const n = Number(this.position.percent_complete)
      if (!Number.isFinite(n)) return 0
      return Math.max(0, Math.min(100, n * 100))
    },
    percentLabel() {
      const n = Number(this.position.percent_complete)
      if (!Number.isFinite(n)) return '—'
      return `${(n * 100).toFixed(2)}%`
    },
    projectLabel() {
      const project = this.contract && this.contract.project
      if (!project) return ''
      return project.project_number ? `${project.project_number} — ${project.name || ''}` : (project.name || '')
    },
    customerGlCode() {
      const contract = this.contract || {}
      const project = contract.project || {}
      return glAccountCode(contract.customer) || glAccountCode(project.customer) || '—'
    },
    hasDraft() {
      return (this.contract.billings || []).some(row => enumValue(row.status) === 'draft')
    },
    canEdit() {
      return this.hasPermission('edit contracts')
    },
    canBill() {
      return this.hasPermission('create invoices') && this.statusValue !== 'closed' && !this.hasDraft
    },
    canHandover() {
      return this.hasPermission('edit contracts') && this.statusValue === 'active'
    },
    canRelease() {
      const held = Number(this.position.retention_held) || 0
      return this.hasPermission('create invoices') && this.statusValue === 'handed_over' && held > 0
    },
    cards() {
      const p = this.position
      return [
        { label: this.$t('ledger_ct_value'), value: this.money(this.contract.contract_value) },
        { label: this.$t('ledger_ct_estimate'), value: this.money(this.contract.estimated_total_cost) },
        { label: this.$t('ledger_ct_cost'), value: this.money(p.cost_to_date) },
        { label: this.$t('ledger_ct_recognized'), value: this.money(p.revenue_recognized) },
        { label: this.$t('ledger_ct_billed'), value: this.money(p.billed_to_date) },
        { label: this.$t('ledger_ct_collected'), value: this.money(p.collected) },
        { label: this.$t('ledger_ct_retention_held'), value: this.money(p.retention_held) },
        { label: this.$t('ledger_ct_remaining'), value: this.money(p.remaining_to_bill) },
      ]
    },
    billingFields() {
      return [
        { key: 'invoice_number', label: this.$t('ledger_inv_number') },
        { key: 'invoice_date', label: this.$t('ledger_date') },
        { key: 'cumulative_work_value', label: this.$t('ledger_ct_cumulative') },
        { key: 'total_amount', label: this.$t('ledger_ct_gross') },
        { key: 'retention_amount', label: this.$t('ledger_ct_retention') },
        { key: 'collectible_amount', label: this.$t('ledger_ct_net') },
        { key: 'balance_due', label: this.$t('ledger_inv_balance') },
        { key: 'status', label: this.$t('ledger_status') },
      ]
    },
    billingPreview() {
      const cumulative = Number(this.billing.cumulative_work_value)
      if (!Number.isFinite(cumulative)) return null
      const billed = Number(this.position.billed_to_date) || 0
      const pct = Number(this.contract.retention_percent) || 0
      const gross = roundMoney(cumulative - billed)
      const retention = roundMoney(gross * pct / 100)
      return {
        billed,
        gross,
        retention,
        net: roundMoney(gross - retention),
        cumulative,
        value: Number(this.contract.contract_value) || 0,
      }
    },
    canSubmitBilling() {
      const preview = this.billingPreview
      if (!preview) return false
      return preview.cumulative > preview.billed && preview.cumulative <= preview.value && preview.gross > 0
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    invoiceVariant(status) {
      return INVOICE_VARIANT[enumValue(status)] || 'secondary'
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    noteError(error) {
      const message = apiErrorMessage(error, this.$t('ledger_error'))
      const key = ledgerMessageKey(message)
      this.toast(key ? this.$t(key) : message, error?.response?.status === 422 ? 'warning' : 'danger')
    },
    openInvoice(payload) {
      const row = oneFrom(payload)
      if (row && row.id && (row.invoice_number || row.kind)) {
        this.$router.push({ name: 'ledger-invoices-view', params: { id: row.id } })
        return true
      }
      return false
    },
    async load() {
      this.loading = true
      try {
        const res = await getContract(this.$route.params.id)
        this.contract = oneFrom(res)
      } catch (error) {
        this.noteError(error)
      } finally {
        this.loading = false
      }
    },
    openBilling() {
      const today = todayIso()
      this.billing = {
        cumulative_work_value: null,
        billing_date: today,
        due_date: addDays(today, 30),
        notes: '',
      }
      this.billingOpen = true
    },
    async submitBilling() {
      if (!this.canSubmitBilling || this.acting) return
      this.acting = true
      try {
        const body = {
          cumulative_work_value: roundMoney(this.billing.cumulative_work_value),
          billing_date: this.billing.billing_date || undefined,
          due_date: this.billing.due_date || undefined,
          notes: String(this.billing.notes || '').trim() || undefined,
        }
        const res = await createContractBilling(this.contract.id, body)
        this.billingOpen = false
        this.toast(this.$t('ledger_ct_billed_ok'), 'success')
        if (!this.openInvoice(res)) await this.load()
      } catch (error) {
        this.noteError(error)
      } finally {
        this.acting = false
      }
    },
    async confirmHandover() {
      const result = await Swal.fire({
        title: this.$t('ledger_ct_handover'),
        text: this.$t('ledger_ct_handover_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_ct_handover'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      try {
        await handoverContract(this.contract.id, { date: todayIso() })
        this.toast(this.$t('ledger_ct_handed'), 'success')
        await this.load()
      } catch (error) {
        this.noteError(error)
      } finally {
        this.acting = false
      }
    },
    async confirmRelease() {
      const result = await Swal.fire({
        title: this.$t('ledger_ct_release'),
        text: this.$t('ledger_ct_release_confirm', { code: this.customerGlCode }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_ct_release'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      try {
        const res = await releaseContractRetention(this.contract.id, { date: todayIso() })
        this.toast(this.$t('ledger_ct_billed_ok'), 'success')
        if (!this.openInvoice(res)) await this.load()
      } catch (error) {
        this.noteError(error)
      } finally {
        this.acting = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-ct-kpis { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
@media (min-width: 1280px) {
  .ledger-ct-kpis { grid-template-columns: repeat(4, 1fr); }
}
.ledger-ct-card { border-bottom: 1px solid #eee; padding: 0.75rem 0; }
.ledger-ct-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex; gap: 0.5rem;
  background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
